import React, { useState } from 'react';
import { Loading } from '@appkit4/react-components/loading';
import {
    useGetTimePeriods,
    useCreateTimePeriod,
    useGetEntitiesForTimePeriod,
    useGetEntitiesNotLinkedToTimePeriod,
    useLinkEntityToTimePeriod,
    useCreateEntity,
    useGetJurisdictions,
    getGetTimePeriodsQueryKey,
    getGetEntitiesForTimePeriodQueryKey,
    getGetEntitiesNotLinkedToTimePeriodQueryKey,
} from '../../api/generated/endpoint';
import { useQueryClient } from '@tanstack/react-query';
import useToast from '../../helpers/useToast';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';
import LinkEntityModal from './setup/LinkEntityModal';
import CreateEntityModal from './setup/CreateEntityModal';
import CreateTimePeriodModal from './setup/CreateTimePeriodModal';

interface EngagementSetupProps {
    children: React.ReactNode;
}

const EngagementSetup: React.FC<EngagementSetupProps> = ({ children }) => {
    const queryClient = useQueryClient();
    const showToast = useToast();
    const showGenericErrorToast = useGenericErrorToast();

    const [createdEntityId, setCreatedEntityId] = useState<number | null>(null);
    const [showCreateEntityInLink, setShowCreateEntityInLink] = useState(false);

    const { data: timePeriods, isLoading: loadingPeriods } =
        useGetTimePeriods();

    const activePeriod = timePeriods?.find((p) => p.isActive);
    const activeTimeId = activePeriod?.timeId;

    const { data: entities, isLoading: loadingEntities } =
        useGetEntitiesForTimePeriod(activeTimeId ?? 0, {
            query: { enabled: !!activeTimeId },
        });

    const { data: unlinkedEntities } = useGetEntitiesNotLinkedToTimePeriod(
        activeTimeId ?? 0,
        { query: { enabled: !!activeTimeId } }
    );

    const { mutate: linkEntity } = useLinkEntityToTimePeriod({
        mutation: {
            onSuccess: () => {
                if (activeTimeId) {
                    queryClient.invalidateQueries({
                        queryKey:
                            getGetEntitiesForTimePeriodQueryKey(activeTimeId),
                    });
                    queryClient.invalidateQueries({
                        queryKey:
                            getGetEntitiesNotLinkedToTimePeriodQueryKey(
                                activeTimeId
                            ),
                    });
                }
                showToast('Success', 'Entity linked successfully', 'success');
            },
            onError: (error) => {
                showGenericErrorToast(error);
            },
        },
    });

    const { mutate: createTimePeriod, isPending: isCreatingPeriod } =
        useCreateTimePeriod({
            mutation: {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: getGetTimePeriodsQueryKey(),
                    });
                    setCreatedEntityId(null);
                    showToast(
                        'Success',
                        'Time period created successfully',
                        'success'
                    );
                },
                onError: (error) => {
                    showGenericErrorToast(error);
                },
            },
        });

    const { mutate: createEntity, isPending: isCreatingEntity } =
        useCreateEntity({
            mutation: {
                onSuccess: (data) => {
                    const newEntityId = data?.entityId;
                    if (newEntityId && activeTimeId) {
                        linkEntity({
                            data: {
                                entityId: newEntityId,
                                timeId: activeTimeId,
                            },
                        });
                        setShowCreateEntityInLink(false);
                    } else {
                        if (newEntityId) {
                            setCreatedEntityId(newEntityId);
                        }
                        showToast(
                            'Success',
                            'Entity created successfully',
                            'success'
                        );
                    }
                },
                onError: (error) => {
                    showGenericErrorToast(error);
                },
            },
        });

    const needsEntity = !entities || entities.length === 0;
    const needsTimePeriod =
        !timePeriods || timePeriods.length === 0 || !activeTimeId;
    const entityQueryEnabled = !!activeTimeId;
    const hasUnlinkedEntities = (unlinkedEntities ?? []).length > 0;

    const showLinkModal =
        needsEntity &&
        !createdEntityId &&
        (!entityQueryEnabled || !loadingEntities) &&
        hasUnlinkedEntities &&
        !showCreateEntityInLink;

    const showCreateModal =
        needsEntity &&
        !createdEntityId &&
        (!entityQueryEnabled || !loadingEntities) &&
        !showLinkModal;

    const { data: jurisdictions } = useGetJurisdictions({
        query: { enabled: showCreateModal },
    });
    const jurisdictionOptions = (jurisdictions ?? []).map((j) => ({
        value: String(j.jurisdictionId),
        label: j.countryName ?? '',
    }));

    if (loadingPeriods) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                }}
            >
                <Loading loadingType="circular" indeterminate />
            </div>
        );
    }

    // On a new engagement the entities query is disabled until a period exists,
    // so needsEntity stays true; createdEntityId is what proves an entity exists.
    const showTimePeriodModal =
        needsTimePeriod && (!needsEntity || !!createdEntityId);
    const showContent = !needsEntity && !needsTimePeriod && !loadingEntities;

    if (loadingEntities && !needsTimePeriod) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                }}
            >
                <Loading loadingType="circular" indeterminate />
            </div>
        );
    }

    return (
        <>
            <LinkEntityModal
                visible={showLinkModal}
                unlinkedEntities={unlinkedEntities ?? []}
                onLink={(entityId) => {
                    if (activeTimeId) {
                        linkEntity({
                            data: { entityId, timeId: activeTimeId },
                        });
                    }
                }}
                onCreateNew={() => setShowCreateEntityInLink(true)}
            />
            <CreateEntityModal
                visible={showCreateModal}
                jurisdictionOptions={jurisdictionOptions}
                isCreating={isCreatingEntity}
                onSubmit={(data) => createEntity({ data })}
            />
            {showTimePeriodModal && (
                <CreateTimePeriodModal
                    isCreating={isCreatingPeriod}
                    onSubmit={(data) =>
                        createTimePeriod({
                            data: {
                                ...data,
                                entityId: createdEntityId ?? undefined,
                            },
                        })
                    }
                />
            )}
            {showContent && <>{children}</>}
        </>
    );
};

export default EngagementSetup;
